import React from 'react'
import { ImageOff, type LucideIcon } from 'lucide-react'
import { ImagePlaceholder } from '@/components/ui/cover-image'
import { imageSrcSet, imageUrl, type ImageOptions } from '@/lib/images'
import { cn } from '@/lib/utils'
import { imageSource, useSignedMedia } from './useSignedMedia'

interface MediaImgProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  mediaId?: string | null
  /** The public URL of an image uploaded before F-76. */
  imageUrl?: string | null
  thumbnailUrl?: string | null
  variant?: 'thumb' | 'full'
  /** Only for the public (older) URL: the CDN rewrite. */
  cdn?: ImageOptions
  /** Only for the public (older) URL with `cdn.w`: also ask for the 2x file through `srcSet`. */
  responsive?: boolean
  /** Module icon on the placeholder shown when there is no image. */
  placeholderIcon?: LucideIcon
}

const DEFAULT_CDN: Record<'thumb' | 'full', ImageOptions> = {
  thumb: { w: 480, fit: 'max' },
  full: { w: 1600, fit: 'max' },
}

/** `object-*` utilities go to the `<img>`; everything else in `className` sizes the slot. */
const objectClasses = (className?: string) =>
  (className ?? '').split(/\s+/).filter((name) => /^object-/.test(name)).join(' ')

/**
 * One image, hosted (signed link, renewed on expiry) or older (public URL).
 *
 * The slot never shows the browser's broken-image icon: while the signed link is on its way or the
 * file is downloading, a pulsing community-colored placeholder fills it, and the picture fades in
 * once decoded. A hosted image that fails is re-signed once (the link may have expired); a second
 * failure, or a failure of an older public URL, leaves the placeholder with an image icon.
 */
export const MediaImg = ({
  mediaId,
  imageUrl: legacyUrl,
  thumbnailUrl,
  variant = 'full',
  cdn,
  responsive = false,
  className,
  alt = '',
  placeholderIcon,
  onLoad,
  onError,
  loading = 'lazy',
  decoding = 'async',
  ...rest
}: MediaImgProps) => {
  const { links, loading: signing, refresh } = useSignedMedia([mediaId])
  const { src: rawSrc, hosted } = imageSource({ mediaId, imageUrl: legacyUrl, thumbnailUrl }, links, variant)
  /* Without an explicit `cdn`, an older public URL still goes through imgix at a width that fits the
     variant, with a 2x srcset. Signed (hosted) links never do: imgix cannot read the private bucket. */
  const resize = cdn ?? DEFAULT_CDN[variant]
  const retina = cdn ? responsive : true
  const src = (hosted ? rawSrc : imageUrl(rawSrc, resize)) || undefined
  const srcSet = !hosted && retina && resize.w ? imageSrcSet(rawSrc, { ...resize, w: resize.w }) : undefined

  const [loadedSrc, setLoadedSrc] = React.useState<string | null>(null)
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null)
  // The media id already re-signed once after a failure, and whether that new request has answered.
  const [retriedFor, setRetriedFor] = React.useState<string | null>(null)
  const [retryAnswered, setRetryAnswered] = React.useState(false)
  const wasSigningRef = React.useRef(signing)

  React.useEffect(() => {
    if (retriedFor && wasSigningRef.current && !signing) setRetryAnswered(true)
    wasSigningRef.current = signing
  }, [signing, retriedFor])

  /** A cached image can finish before React attaches `onLoad`. */
  const imgRef = React.useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) setLoadedSrc(node.getAttribute('src'))
  }, [])

  const linkError = !!(mediaId && links[mediaId]?.error)
  const awaitingLink = !!mediaId && !src && !linkError
  const awaitingRetry = !!src && failedSrc === src && retriedFor === mediaId && !retryAnswered
  const failed = linkError || (!!src && failedSrc === src && !awaitingRetry)

  if (failed || !src) {
    const busy = awaitingLink
    return (
      <ImagePlaceholder
        className={cn(busy && 'animate-pulse', className)}
        icon={failed ? ImageOff : placeholderIcon}
        iconClassName="h-6 w-6"
        role={failed ? 'img' : undefined}
        aria-label={failed ? alt : undefined}
        aria-busy={busy || undefined}
      />
    )
  }

  const shown = loadedSrc === src && failedSrc !== src
  return (
    <span className={cn('relative block overflow-hidden', className)}>
      {!shown && (
        <ImagePlaceholder
          className="absolute inset-0 h-full w-full animate-pulse"
          icon={placeholderIcon}
          iconClassName="h-6 w-6"
          aria-busy
        />
      )}
      {failedSrc !== src && (
        <img
          {...rest}
          ref={imgRef}
          src={src}
          srcSet={srcSet}
          alt={alt}
          loading={loading}
          decoding={decoding}
          className={cn(
            'relative block h-full w-full transition-opacity duration-200',
            objectClasses(className),
            shown ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={(e) => { setLoadedSrc(src); onLoad?.(e) }}
          onError={(e) => {
            setFailedSrc(src)
            if (hosted && mediaId && retriedFor !== mediaId) {
              setRetriedFor(mediaId)
              setRetryAnswered(false)
              refresh()
            }
            onError?.(e)
          }}
        />
      )}
    </span>
  )
}
